import React, { useState } from 'react';
import Header from '../../../components/header/Header';
import Footer from '../../../components/footer/Footer';
import LoginModal from '../../../components/loginSection/LoginModal';
import './Terms.css';
import { appConfig } from '../../config/appConfig';

const Terms = () => {
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('terms');

    const openLoginModal = () => setIsLoginModalOpen(true);
    const closeLoginModal = () => setIsLoginModalOpen(false);

    const termsContent = [
        {
            title: '1. Introduction',
            content: `${appConfig.appName} ${appConfig.messaging.legalIntro} By accessing or using our platform, you agree to comply with and be bound by these terms and conditions.`
        },
        {
            title: '2. User Accounts',
            content: 'To use certain features of the platform, you must create an account. You are responsible for maintaining the confidentiality of your account information and for all activities that occur under your account. You must provide accurate and complete information when creating your account.'
        },
        {
            title: '3. Privacy Policy',
            content: 'Your privacy is important to us. Our Privacy Policy explains how we collect, use, and protect your personal information. By using our platform, you consent to our collection and use of your information as outlined in the Privacy Policy.'
        },
        {
            title: '4. Use of the Platform',
            content: 'You agree to use the platform for lawful purposes only. You must not use the platform in any way that causes, or may cause, damage to the platform or impairment of the availability or accessibility of the platform. Prohibited activities include attempting to gain unauthorized access, transmitting harmful code, or engaging in any activity that disrupts our services.'
        },
        {
            title: '5. Intellectual Property',
            content: `All content on the platform, including text, graphics, logos, and images, is the property of ${appConfig.appName} or its content suppliers and is protected by copyright and other intellectual property laws. You may not reproduce, distribute, or create derivative works without our express permission.`
        },
        {
            title: '6. User Content',
            content: `By submitting content to the platform (scores, reviews, comments), you grant ${appConfig.appName} a non-exclusive, worldwide license to use, display, and distribute that content. You retain ownership of your content but are responsible for ensuring it does not violate any laws or third-party rights.`
        },
        {
            title: '7. Limitation of Liability',
            content: `${appConfig.appName} is provided "as is" without warranties of any kind. We shall not be liable for any indirect, incidental, or consequential damages arising from your use of the platform. Our total liability shall not exceed the amount you have paid us in the preceding 12 months.`
        },
        {
            title: '8. Changes to Terms',
            content: 'We reserve the right to modify these terms at any time. We will notify users of significant changes via email or platform notification. Your continued use of the platform after changes constitutes acceptance of the new terms.'
        }
    ];

    const privacyContent = [
        {
            title: '1. Information We Collect',
            content: `We collect personal information such as your name, email address, location, mobile number, and profile information when you create an account or use our platform. ${appConfig.messaging.legalDataSummary}`
        },
        {
            title: '2. How We Use Your Information',
            content: 'We use your information to provide and improve our services, communicate with you, and personalize your experience on the platform. This includes displaying scores, connecting you with clubs, and sending relevant notifications about events or features.'
        },
        {
            title: '3. Information Sharing',
            content: 'We do not sell your personal information. We share data with third parties only as necessary to provide our services (such as payment processors), comply with the law, or protect our rights. You can control the visibility of your profile and scores in your settings.'
        },
        {
            title: '4. Data Security',
            content: 'We implement industry-standard security measures including encryption, secure servers, and regular security audits to protect your personal information from unauthorized access, use, or disclosure. However, no method of transmission over the internet is 100% secure.'
        },
        {
            title: '5. Data Retention',
            content: 'We retain your personal data for as long as your account is active or as needed to provide you services. You can request deletion of your account and associated data at any time by contacting our support team.'
        },
        {
            title: '6. Your Rights',
            content: `Under GDPR and other privacy laws, you have the right to access, correct, or delete your personal data. You can also object to processing, request data portability, or withdraw consent at any time. To exercise these rights, contact us at ${appConfig.privacyEmail}.`
        },
        {
            title: '7. Cookies',
            content: 'We use cookies and similar tracking technologies to improve your experience, analyze usage patterns, and deliver personalized content. You can manage cookie preferences through your browser settings, though some features may not function properly without them.'
        },
        {
            title: '8. Updates to This Policy',
            content: 'We may update this Privacy Policy periodically. We will notify you of any material changes by posting the new policy on this page and updating the "Last Updated" date. We encourage you to review this policy regularly.'
        }
    ];

    return (
        <div className="terms-page">
            <Header openLoginModal={openLoginModal} />

            {/* Hero Section */}
            <section className="terms-hero">
                <div className="terms-hero-bg"></div>
                <div className="container">
                    <div className="terms-hero-content">
                        <span className="terms-badge">Legal</span>
                        <h1 className="terms-hero-title">
                            Terms & Privacy
                        </h1>
                        <p className="terms-hero-subtitle">
                            Understanding how we protect your data and the guidelines for using {appConfig.appDisplayName}.
                        </p>
                        <p className="terms-updated">Last updated: March 2026</p>
                    </div>
                </div>
            </section>

            {/* Content Section */}
            <section className="terms-content-section">
                <div className="container">
                    <div className="terms-layout">
                        {/* Sidebar */}
                        <aside className="terms-sidebar">
                            <nav className="terms-nav">
                                <button 
                                    className={`terms-nav-btn ${activeTab === 'terms' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('terms')}
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                        <polyline points="14 2 14 8 20 8"></polyline>
                                        <line x1="16" y1="13" x2="8" y2="13"></line>
                                        <line x1="16" y1="17" x2="8" y2="17"></line>
                                        <polyline points="10 9 9 9 8 9"></polyline>
                                    </svg>
                                    Terms & Conditions
                                </button>
                                <button 
                                    className={`terms-nav-btn ${activeTab === 'privacy' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('privacy')}
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                    </svg>
                                    Privacy Policy
                                </button>
                            </nav>
                            
                            <div className="terms-contact">
                                <h4>Questions?</h4>
                                <p>Contact our team for any queries about our policies.</p>
                                <a href={`mailto:${appConfig.supportEmail}`} className="terms-contact-link">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                        <polyline points="22,6 12,13 2,6"></polyline>
                                    </svg>
                                    {appConfig.supportEmail}
                                </a>
                            </div>
                        </aside>

                        {/* Main Content */}
                        <main className="terms-main">
                            <div className="terms-card">
                                <div className="terms-card-header">
                                    <h2>
                                        {activeTab === 'terms' ? 'Terms & Conditions' : 'Privacy Policy'}
                                    </h2>
                                    <p>
                                        {activeTab === 'terms' 
                                            ? `Welcome to ${appConfig.appName}! By using our platform and mobile app, you agree to the following terms and conditions.`
                                            : `This Privacy Policy describes how ${appConfig.appName} collects, uses, and protects your personal information when you use our platform and mobile app.`
                                        }
                                    </p>
                                </div>

                                <div className="terms-sections">
                                    {(activeTab === 'terms' ? termsContent : privacyContent).map((section, index) => (
                                        <div key={index} className="terms-section">
                                            <h3>{section.title}</h3>
                                            <p>{section.content}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </main>
                    </div>
                </div>
            </section>

            <LoginModal isOpen={isLoginModalOpen} onClose={closeLoginModal} />
            <Footer />
        </div>
    );
};

export default Terms;
